import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { plans, type Plan } from '../api/plans';
import { IconCalendarOff, IconClipboardList, IconRefreshCw, IconTriangleAlert } from '../icons';
import { formatDate } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Alert } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';

/**
 * Görevlerim — the visits planned for the person reading this.
 *
 * <p>The auditor's side of planning, and deliberately the thinnest screen in the product:
 * one list, soonest first, one button. Somebody standing in a car park at nine in the
 * morning wants to know which store and to start — anything else on this screen is between
 * them and the work.</p>
 */
export function Tasks() {
  const t = useTranslate();
  const navigate = useNavigate();

  const [rows, setRows] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await plans.list({ mine: true, open: true });
      setRows(list.rows);
      setError(null);
    } catch (e) {
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Opens the visit and goes straight into it.
   *
   * <p>No confirmation step. Starting is not a commitment somebody needs protecting from —
   * a draft can be thrown away — and a dialog between the auditor and the checklist is one
   * tap too many on a phone held in one hand.</p>
   */
  const start = async (plan: Plan) => {
    setStarting(plan.id);
    try {
      const { auditId } = await plans.start(plan.id);
      navigate(`/denetimler/${auditId}`);
    } catch (e) {
      setError(describeError(e, 'error.saveFailed'));
      setStarting(null);
    }
  };

  const overdue = rows.filter((r) => r.isOverdue).length;

  return (
    <>
      <PageHeader
        title={t('tasks.title')}
        subtitle={t('tasks.subtitle')}
        action={
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <IconRefreshCw className={loading ? 'nv-spin' : undefined} />
            {t('map.refresh')}
          </Button>
        }
        aside={
          overdue > 0 ? (
            <Badge tone="critical">{t('tasks.overdueCount', { count: String(overdue) })}</Badge>
          ) : null
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      {!loading && rows.length === 0 ? (
        <EmptyState
          icon={IconClipboardList}
          title={t('tasks.empty')}
          text={t('tasks.emptyText')}
        />
      ) : (
        <div className="nv-tasks">
          {rows.map((plan) => (
            <Card key={plan.id} className={plan.isOverdue ? 'nv-task is-overdue' : 'nv-task'}>
              <div className="nv-task__head">
                <div>
                  <strong>{plan.locationCode}</strong> {plan.locationName}
                  {plan.city ? <span className="nv-task__city">{plan.city}</span> : null}
                </div>

                {plan.isOverdue ? (
                  <Badge tone="critical" icon={IconTriangleAlert}>
                    {t('tasks.overdue')}
                  </Badge>
                ) : plan.status === 'Started' ? (
                  <Badge tone="comment">{t('tasks.started')}</Badge>
                ) : null}
              </div>

              <dl className="nv-task__facts">
                <dt>{t('tasks.form')}</dt>
                <dd>{plan.formName}</dd>

                <dt>{t('tasks.period')}</dt>
                <dd>
                  {plan.from === plan.to
                    ? formatDate(plan.from)
                    : `${formatDate(plan.from)} – ${formatDate(plan.to)}`}
                </dd>

                <dt>{t('tasks.plannedBy')}</dt>
                <dd>{plan.plannedByName || '—'}</dd>
              </dl>

              {plan.note ? <p className="nv-task__note">{plan.note}</p> : null}

              <Button onClick={() => void start(plan)} disabled={starting !== null}>
                {plan.status === 'Started' ? t('tasks.continue') : t('tasks.start')}
              </Button>
            </Card>
          ))}
        </div>
      )}

      {!loading && rows.length > 0 ? (
        <p className="nv-tasks__foot">
          <IconCalendarOff />
          {t('tasks.foot')}
        </p>
      ) : null}
    </>
  );
}
